import { z } from "zod";
import { EXPENSE_STATUS } from "./expenses.js";
// 🔴 US-E20.F3-0209 (@UC9332): chi khẩn cấp CŨNG post EXPENSE ngay ⇒ CŨNG phải khai mục đích khi quỹ
// có ràng buộc (không có cửa sau "khẩn cấp" để tiêu tiền earmarked sai mục đích).
import { purposeTagSchema } from "./earmark.js";

/* ===========================================================================
 * US-E17.F4-0191 — CHI KHẨN CẤP (@UC9262 @UC9263 @UC9264) · P3.3 "chi trước – hợp thức hoá sau"
 *
 * POST /v1/funds/:id/expenses/emergency — chi NGAY (post EXPENSE ledger, KHÔNG chờ vote) rồi mở phiên
 * TRUY NHẬN (ratify) 48h. Bác ⇒ ghi nợ người chi ↔ quỹ (KHÔNG reverse — tiền đã chi thật).
 *
 * 🔴 BẮT BUỘC bằng chứng: `reason` (lý do) + `receiptKeys` (≥1 KEY ảnh hoá đơn đã upload R2 — lưu KEY
 * BỀN, KHÔNG URL, mirror 0040 H2). Thiếu ⇒ 400 tại contract (fail-loud, tiền KHÔNG ra).
 * Tiền STRING minor unit (B1, no float).
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** Khoá registry `system_settings` — hạn truy nhận (giờ). MIRROR domain `emergency-expense.ts`. */
export const EMERGENCY_RATIFY_HOURS_KEY = "emergency_ratify_hours";
/** Mặc định 48h (P3.3). */
export const EMERGENCY_RATIFY_HOURS_DEFAULT = 48;
export const emergencyRatifyHoursSchema = z.number().int().min(1).max(720);

/** `kind` DebtEntry sinh khi phiên truy nhận BỊ BÁC. MIRROR domain + CHECK `debt_entries_kind_check`. */
export const DEBT_KIND_EMERGENCY_REPAY = "emergency_repay";

export const EMERGENCY_REASON_MAX = 500;
export const EMERGENCY_RECEIPT_MAX_COUNT = 3;

export const EMERGENCY_REASON_REQUIRED_MESSAGE =
  "Chi khẩn cấp bắt buộc nêu lý do.";
export const EMERGENCY_RECEIPT_REQUIRED_MESSAGE =
  "Chi khẩn cấp bắt buộc đính ít nhất 1 ảnh hoá đơn.";

/**
 * Body chi khẩn cấp. `receiptKeys` = KEY R2 của ảnh hoá đơn ĐÃ upload (client upload trước qua đường
 * biên lai sẵn có). `.strict()` chống over-post. superRefine ⇒ thiếu bằng chứng là LỖI CONTRACT.
 */
export const createEmergencyExpenseRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    amountMinor: z
      .string()
      .regex(MONEY_INTEGER_STRING_PATTERN)
      // ⚠️ refine chạy KỂ CẢ khi regex đã fail (zod ZodEffects) ⇒ phải tự chặn chuỗi không-nguyên
      // TRƯỚC khi BigInt() (float/âm ⇒ BigInt ném SyntaxError = 500 thay vì 400). Fail-loud đúng bậc.
      .refine(
        (v) => MONEY_INTEGER_STRING_PATTERN.test(v) && BigInt(v) > 0n,
        "Số tiền phải lớn hơn 0",
      )
      .refine(
        (v) =>
          !MONEY_INTEGER_STRING_PATTERN.test(v) ||
          BigInt(v) <= 9223372036854775807n,
        "Số tiền vượt giới hạn cho phép.",
      ),
    category: z.string().trim().max(100).optional(),
    /** 🔴 BẮT BUỘC — lý do chi khẩn cấp (dấu vết chống chối cãi). */
    reason: z.string().trim().max(EMERGENCY_REASON_MAX).optional(),
    /** 🔴 BẮT BUỘC ≥1 — KEY ảnh hoá đơn (KHÔNG URL). */
    receiptKeys: z
      .array(z.string().trim().min(1).max(500))
      .max(EMERGENCY_RECEIPT_MAX_COUNT)
      .optional(),
    /** 🔴 @UC9332 — mục đích ràng buộc (quỹ earmark). Optional contract, BẮT BUỘC-ĐIỀU-KIỆN ở service. */
    purposeTag: purposeTagSchema.optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.reason == null || val.reason.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: EMERGENCY_REASON_REQUIRED_MESSAGE,
        path: ["reason"],
      });
    }
    if (val.receiptKeys == null || val.receiptKeys.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: EMERGENCY_RECEIPT_REQUIRED_MESSAGE,
        path: ["receiptKeys"],
      });
    }
  });

export type CreateEmergencyExpenseRequest = z.infer<
  typeof createEmergencyExpenseRequestSchema
>;

/**
 * Response: expense ĐÃ `posted` (tiền ra ngay) + ledgerTxId (EXPENSE advance) + phiên truy nhận đang
 * `open` với deadline (now + emergency_ratify_hours).
 */
export const createEmergencyExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    fundId: z.string().uuid(),
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    /** Đã chi THẬT ngay lúc tạo (không chờ phiếu). */
    status: z.literal(EXPENSE_STATUS.POSTED),
    ledgerTxId: z.string().uuid(),
    /** Phiên truy nhận (ratify) mở cùng tx — fail-closed: không có "đã chi mà không phiếu". */
    consensusId: z.string().uuid(),
    ratifyDeadline: z.string().datetime(),
    postedAt: z.string().datetime(),
  })
  .strict();

export type CreateEmergencyExpenseResponse = z.infer<
  typeof createEmergencyExpenseResponseSchema
>;

/**
 * 🔴 CONFLICT-CODE-ROUND-2 (@UC9262) — 409 (giữ status cũ) khi quỹ của khoản chi khẩn cấp không còn (xoá giữa chừng). Client map `code → message`.
 */
export const EMERGENCY_EXPENSE_FUND_NOT_FOUND_CODE = "EMERGENCY_EXPENSE_FUND_NOT_FOUND" as const;
